import React, { useMemo, useState } from 'react';

import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { MultipleValue } from '@shared/table/Rules/component';
import TableIndex from '@shared/table/TableIndex';
import type { SupportRule, TableService } from '@shared/table/TableIndex';

import ProductSerialNumberDrawer from './ProductSerialNumberDrawer';

// ----------------------------------------------------------------------------
// Service contracts
// 對齊 useAdminProductSerialNumberService / useOperatorProductSerialNumberService
// (Task 6) — 我們只需要 getRecords (TableIndex 用) 與 getById (Task 8 Drawer 用)。
//
// `getRecords` 回傳 envelope `{ success, message, data, has_more, ... }`，
// TableIndex/Context 直接讀 `data` + `has_more` — passthrough 兼容，無需 adapter。
// ----------------------------------------------------------------------------

export interface ProductSerialNumberServiceLike {
  getRecords: (params: Record<string, unknown>) => Promise<{ data: unknown[]; has_more: boolean }>;
  getById: (id: string) => Promise<unknown>;
}

// 商品多選 filter 用的 lazyLoad — caller 可選擇注入 (參考 OutOfStockProductsList 的
// `brandService` pattern)。若未注入則「商品篩選」rule 不會出現。
export interface ProductServiceLike {
  fetchList: (params: Record<string, unknown>) => Promise<{
    success?: boolean;
    data?: Array<{ id: string; name: string; [key: string]: unknown }>;
    has_more?: boolean;
  }>;
}

// 篩選器可選的 status（filter UI option list）。
// 注意：刻意省略 'disposed' —— Ruby `ProductSerialNumber` enum 目前只有
// in_stock / shipped / returned_pending（見 app/models/product_serial_number.rb），
// disposition flow 上線前選 'disposed' 永遠回空、誤導使用者。
// Zod schema (services/shared/...) 仍保留 'disposed' 作為 forward-compat，
// 待 BE enum 加上 'disposed' 後再把它放回此陣列。
const STATUS_FILTER_VALUES = ['in_stock', 'shipped', 'returned_pending'] as const;
// 完整 row.status 型別仍須涵蓋 'disposed'，這樣 Tag 顯示 cell 在未來 BE 回傳
// disposed PSN 時不會 TS error；i18n key 與 Zod schema 都已預埋。
type PsnStatus = 'in_stock' | 'shipped' | 'returned_pending' | 'disposed';

// 列表 row shape — 對齊 controller `list_item` (admin / operator 同 shape)
// 與 ProductSerialNumberRecordSchema。
interface ProductSerialNumberRow {
  id: string;
  number: string;
  status: PsnStatus;
  product: {
    id: string;
    name: string;
    sku: string;
  };
  inventory: {
    id: string;
    warehouse: { id: string; name: string };
    batch?: string | null;
    inventory_type?: { id: string; name: string } | null;
    status?: string | null;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface ProductSerialNumberListProps {
  role: 'admin' | 'operator';
  service: ProductSerialNumberServiceLike;
  /**
   * 用於商品多選 filter 的 lazyLoad 來源。未注入則商品 filter 不顯示。
   * Admin / Operator 的 page caller 各自傳入自己的 product service。
   */
  productService?: ProductServiceLike;
}

const ProductSerialNumberList: React.FC<ProductSerialNumberListProps> = ({
  role,
  service,
  productService,
}) => {
  const { t } = useTranslation();

  // selectedId 被設後 Task 8 的 Drawer 會接手；此 Task 先用 placeholder 顯示。
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const placeholder = t(
    'shared.product_serial_numbers.drawer.no_inventory_placeholder',
    '—',
  );

  // ---------------------------------------------------------------------------
  // Columns (per spec § 3.5)
  //   number / product / status / warehouse / batch / inventory_type / updated_at
  // 對齊 TableIndex 的 column DSL (參考 OrderSerialNumbersList)
  // ---------------------------------------------------------------------------
  const columns = useMemo(
    () => [
      {
        nameKey: 'shared.product_serial_numbers.columns.number',
        name: t('shared.product_serial_numbers.columns.number'),
        persist: true,
        sortable: true,
        sortFieldName: 'product_serial_numbers.number',
        width: '200px',
        wrap: true,
        cell: (row: ProductSerialNumberRow) => (
          <a
            data-testid={`psn-number-link-${row.id}`}
            onClick={(e) => {
              e.preventDefault();
              setSelectedId(row.id);
            }}
          >
            {row.number}
          </a>
        ),
      },
      {
        nameKey: 'shared.product_serial_numbers.columns.product',
        name: t('shared.product_serial_numbers.columns.product'),
        persist: true,
        sortable: false,
        width: '260px',
        wrap: true,
        cell: (row: ProductSerialNumberRow) => (
          <div>
            <div>
              <Link to={`/products/${row.product.id}`}>{row.product.name}</Link>
            </div>
            <div style={{ color: '#999', fontSize: '12px' }}>{row.product.sku}</div>
          </div>
        ),
      },
      {
        nameKey: 'shared.product_serial_numbers.columns.status',
        name: t('shared.product_serial_numbers.columns.status'),
        persist: true,
        sortable: true,
        sortFieldName: 'product_serial_numbers.status',
        width: '140px',
        wrap: true,
        cell: (row: ProductSerialNumberRow) => (
          <Tag>{t(`shared.product_serial_numbers.filters.status.${row.status}`)}</Tag>
        ),
      },
      {
        nameKey: 'shared.product_serial_numbers.columns.warehouse',
        name: t('shared.product_serial_numbers.columns.warehouse'),
        sortable: false,
        width: '160px',
        wrap: true,
        selector: (row: ProductSerialNumberRow) =>
          row.inventory?.warehouse?.name ?? placeholder,
      },
      {
        nameKey: 'shared.product_serial_numbers.columns.batch',
        name: t('shared.product_serial_numbers.columns.batch'),
        sortable: false,
        width: '140px',
        wrap: true,
        selector: (row: ProductSerialNumberRow) =>
          row.inventory?.batch ?? placeholder,
      },
      {
        nameKey: 'shared.product_serial_numbers.columns.inventory_type',
        name: t('shared.product_serial_numbers.columns.inventory_type'),
        sortable: false,
        width: '160px',
        wrap: true,
        selector: (row: ProductSerialNumberRow) =>
          row.inventory?.inventory_type?.name ?? placeholder,
      },
      {
        nameKey: 'shared.product_serial_numbers.columns.updated_at',
        name: t('shared.product_serial_numbers.columns.updated_at'),
        sortable: true,
        sortFieldName: 'product_serial_numbers.updated_at',
        width: '160px',
        wrap: true,
        selector: (row: ProductSerialNumberRow) => row.updated_at,
      },
    ],
    [t, placeholder],
  );

  // ---------------------------------------------------------------------------
  // Status filter options — admin / operator 同樣 3 個可選。
  // 'disposed' 故意省略（見 STATUS_FILTER_VALUES 上方註解）。
  // ---------------------------------------------------------------------------
  const statusOptions = useMemo(
    () =>
      STATUS_FILTER_VALUES.map((value) => ({
        value,
        label: t(`shared.product_serial_numbers.filters.status.${value}`),
      })),
    [t],
  );

  // ---------------------------------------------------------------------------
  // Support rules (filters)
  //   - numbers: 多行 textarea (createable=true → MultipleValue 渲染為 TextArea，
  //              Rules/Content.jsx 會自動 split by newline/comma 送成 array)
  //   - product_ids: 多選 + lazyLoad (僅在 productService 注入時顯示)
  //   - status: enum 多選 (defaultOptions 直接寫 4 選項)
  //
  // 送出時 inputName 直接就是 backend 接受的 param key
  //   (`numbers`, `product_ids`, `status` — 見 controller#get_records)
  // ---------------------------------------------------------------------------
  const supportRules = useMemo<SupportRule[]>(() => {
    const rules: SupportRule[] = [
      {
        label: t('shared.product_serial_numbers.filters.numbers.label'),
        component: MultipleValue,
        inputName: 'numbers',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.product_serial_numbers.filters.numbers.tooltip_title'),
          content: t('shared.product_serial_numbers.filters.numbers.tooltip_content'),
        },
      },
    ];

    if (productService) {
      rules.push({
        label: t('shared.product_serial_numbers.filters.product_ids.label'),
        component: MultipleValue,
        inputName: 'product_ids',
        defaultPinned: true,
        createable: false,
        lazyLoad: {
          action: (params: Record<string, unknown>) => productService.fetchList(params),
          transform: (payload: { data?: Array<{ id: string; name: string }> }) =>
            (payload.data ?? []).map((p) => ({ label: p.name, value: p.id })),
        },
      });
    }

    rules.push({
      label: t('shared.product_serial_numbers.filters.status.label'),
      component: MultipleValue,
      inputName: 'status',
      defaultPinned: true,
      createable: false,
      // 不走 lazyLoad — 直接給 4 個 enum option（含 disabled 處理）
      defaultOptions: statusOptions,
    });

    return rules;
  }, [t, productService, statusOptions]);

  // 暫存給 Task 8 Drawer 使用的 service / selectedId — TableService 介面要求即可。
  const tableService = service as unknown as TableService;

  return (
    <>
      <TableIndex
        columns={columns}
        searchable={false}
        columnsEditable
        tableId={`${role}-product-serial-numbers`}
        defaultQuery={{
          sorting_field: 'product_serial_numbers.updated_at',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        service={tableService}
        testIdPrefix="psn"
      />

      <ProductSerialNumberDrawer
        role={role}
        service={service}
        psnId={selectedId}
        open={selectedId !== null}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
};

export default ProductSerialNumberList;
